/**
 * Phase 3a §5 — Underwriting outcomes panel (admin-only).
 *
 * Rendered on the AdminSubmissionDetail page for any employer that has an
 * `employer_id`. Two rows (approved coverage, effective date), each with
 * current value + history + an edit modal. Save → POSTs to the matching
 * `/api/admin/employer-aggregate/{employer_id}/...` endpoint, which appends
 * to the history array server-side.
 *
 * This component owns the modals; the parent owns the block placement.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    adminSetCoverageApproved,
    adminSetCoverageEffectiveDate,
    adminUnderwritingOutcomesRead,
} from "../../lib/admin";
import { track } from "../../lib/analytics";

function fmtMoney(cents) {
    if (cents == null || cents === 0) return null;
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(cents / 100);
}

function fmtDateISO(v) {
    if (!v) return null;
    const d = typeof v === "string" ? new Date(v) : v;
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
    });
}

function fmtStamp(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}


/* -------------------------------------------------------------- Modal UI */
function OutcomeModal({ open, onClose, onSave, title, sub, children, saveLabel, savingLabel, cancelLabel, disabled, saving, error }) {
    if (!open) return null;
    return (
        <div
            className="uw-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="uw-modal"
        >
            <div className="uw-modal">
                <div className="uw-modal-head">
                    <h3>{title}</h3>
                    {sub && <p className="uw-modal-sub">{sub}</p>}
                </div>
                <div className="uw-modal-body">{children}</div>
                {error && <div className="review-err" data-testid="uw-modal-error">{error}</div>}
                <div className="uw-modal-foot">
                    <button
                        type="button"
                        className="admin-btn secondary"
                        onClick={onClose}
                        disabled={saving}
                        data-testid="uw-modal-cancel"
                    >{cancelLabel}</button>
                    <button
                        type="button"
                        className="admin-btn primary"
                        onClick={onSave}
                        disabled={disabled || saving}
                        data-testid="uw-modal-save"
                    >{saving ? savingLabel : saveLabel}</button>
                </div>
            </div>
        </div>
    );
}


/* -------------------------------------------------------- Main panel */
export default function UnderwritingOutcomesPanel({ employerId }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);

    const [approvedOpen, setApprovedOpen] = useState(false);
    const [approvedDollars, setApprovedDollars] = useState("");
    const [approvedNotes, setApprovedNotes] = useState("");

    const [effectiveOpen, setEffectiveOpen] = useState(false);
    const [effectiveValue, setEffectiveValue] = useState("");
    const [effectiveNotes, setEffectiveNotes] = useState("");

    const [saving, setSaving] = useState(false);
    const [modalErr, setModalErr] = useState(null);

    const load = useCallback(async () => {
        try {
            const d = await adminUnderwritingOutcomesRead(employerId);
            setData(d);
            setLoading(false);
        } catch (e) {
            setErr(e?.response?.data?.detail || t("admin.underwriting.errors.generic"));
            setLoading(false);
        }
    }, [employerId, t]);

    useEffect(() => { load(); }, [load]);

    /* -------- Approved coverage modal handlers -------- */
    const openApproved = () => {
        setApprovedDollars(
            data?.coverage_approved_total_cents
                ? (data.coverage_approved_total_cents / 100).toFixed(2)
                : "",
        );
        setApprovedNotes(data?.coverage_approved_notes || "");
        setModalErr(null);
        setApprovedOpen(true);
    };
    const saveApproved = async () => {
        setModalErr(null);
        // Accept decimal dollars (e.g. "999999.99"); strip non-digit/dot chars.
        const cleaned = (approvedDollars || "").replace(/[^\d.]/g, "");
        const parsed = parseFloat(cleaned);
        if (Number.isNaN(parsed) || parsed < 0) {
            setModalErr(t("admin.underwriting.modalApproved.invalid"));
            return;
        }
        const valueCents = Math.round(parsed * 100);
        setSaving(true);
        try {
            const res = await adminSetCoverageApproved(
                employerId,
                valueCents,
                (approvedNotes || "").trim(),
            );
            setData(res);
            track("admin_coverage_approved_set", { employer_id: employerId, value_cents: valueCents });
            setApprovedOpen(false);
        } catch (e) {
            setModalErr(e?.response?.data?.detail || t("admin.underwriting.errors.generic"));
        } finally {
            setSaving(false);
        }
    };

    /* -------- Effective date modal handlers -------- */
    const openEffective = () => {
        const cur = data?.coverage_effective_date;
        setEffectiveValue(cur ? String(cur).slice(0, 10) : "");
        setEffectiveNotes("");
        setModalErr(null);
        setEffectiveOpen(true);
    };
    const saveEffective = async () => {
        setModalErr(null);
        // Expect YYYY-MM-DD
        if (!/^\d{4}-\d{2}-\d{2}$/.test((effectiveValue || "").trim())) {
            setModalErr(t("admin.underwriting.modalEffective.invalid"));
            return;
        }
        setSaving(true);
        try {
            const res = await adminSetCoverageEffectiveDate(
                employerId,
                effectiveValue.trim(),
                (effectiveNotes || "").trim(),
            );
            setData(res);
            track("admin_coverage_effective_date_set", { employer_id: employerId, value: effectiveValue.trim() });
            setEffectiveOpen(false);
        } catch (e) {
            setModalErr(e?.response?.data?.detail || t("admin.underwriting.errors.generic"));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="detail-section uw-panel" data-testid="uw-panel-loading">
                <h3>{t("admin.underwriting.title")}</h3>
                <div style={{ color: "var(--muted)", fontStyle: "italic" }}>Loading…</div>
            </div>
        );
    }
    if (err && !data) {
        return (
            <div className="detail-section uw-panel">
                <h3>{t("admin.underwriting.title")}</h3>
                <div className="review-err" data-testid="uw-panel-err">{String(err)}</div>
            </div>
        );
    }

    const approvedDisplay = fmtMoney(data.coverage_approved_total_cents);
    const effectiveDisplay = fmtDateISO(data.coverage_effective_date);

    return (
        <div className="detail-section uw-panel" data-testid="uw-panel">
            <h3>{t("admin.underwriting.title")}</h3>
            <p className="admin-sub" style={{ marginTop: 0 }}>{t("admin.underwriting.sub")}</p>

            {/* ---------- Approved coverage block ---------- */}
            <div className="uw-block" data-testid="uw-block-approved">
                <div className="uw-block-head">
                    <div className="uw-block-label mono">{t("admin.underwriting.blocks.coverageApproved.label")}</div>
                    <button
                        type="button"
                        className="admin-btn secondary"
                        onClick={openApproved}
                        data-testid="uw-edit-approved"
                    >{t("admin.underwriting.blocks.coverageApproved.edit")}</button>
                </div>
                <div className="uw-block-value" data-testid="uw-value-approved">
                    {approvedDisplay || (
                        <span className="uw-empty">{t("admin.underwriting.blocks.coverageApproved.empty")}</span>
                    )}
                </div>
                {(data.coverage_approved_set_by || data.coverage_approved_set_at) && (
                    <div className="uw-block-meta mono">
                        {data.coverage_approved_set_by ? `${t("admin.underwriting.blocks.coverageApproved.setBy")} ${data.coverage_approved_set_by}` : ""}
                        {data.coverage_approved_set_by && data.coverage_approved_set_at ? " · " : ""}
                        {data.coverage_approved_set_at ? fmtStamp(data.coverage_approved_set_at) : ""}
                    </div>
                )}
                {data.coverage_approved_notes && (
                    <div className="uw-block-notes">{data.coverage_approved_notes}</div>
                )}
                <HistoryTable
                    entries={data.coverage_approved_history}
                    kind="money"
                    t={t}
                    testId="uw-history-approved"
                />
            </div>

            {/* ---------- Effective date block ---------- */}
            <div className="uw-block" data-testid="uw-block-effective">
                <div className="uw-block-head">
                    <div className="uw-block-label mono">{t("admin.underwriting.blocks.coverageEffectiveDate.label")}</div>
                    <button
                        type="button"
                        className="admin-btn secondary"
                        onClick={openEffective}
                        data-testid="uw-edit-effective"
                    >{t("admin.underwriting.blocks.coverageEffectiveDate.edit")}</button>
                </div>
                <div className="uw-block-value" data-testid="uw-value-effective">
                    {effectiveDisplay || (
                        <span className="uw-empty">{t("admin.underwriting.blocks.coverageEffectiveDate.empty")}</span>
                    )}
                </div>
                {(data.coverage_effective_date_set_by || data.coverage_effective_date_set_at) && (
                    <div className="uw-block-meta mono">
                        {data.coverage_effective_date_set_by ? `${t("admin.underwriting.blocks.coverageEffectiveDate.setBy")} ${data.coverage_effective_date_set_by}` : ""}
                        {data.coverage_effective_date_set_by && data.coverage_effective_date_set_at ? " · " : ""}
                        {data.coverage_effective_date_set_at ? fmtStamp(data.coverage_effective_date_set_at) : ""}
                    </div>
                )}
                <HistoryTable
                    entries={data.coverage_effective_date_history}
                    kind="date"
                    t={t}
                    testId="uw-history-effective"
                />
            </div>

            {/* ------- Modals ------- */}
            <OutcomeModal
                open={approvedOpen}
                onClose={() => !saving && setApprovedOpen(false)}
                onSave={saveApproved}
                title={t("admin.underwriting.modalApproved.title")}
                sub={t("admin.underwriting.modalApproved.sub")}
                saveLabel={t("admin.underwriting.modalApproved.save")}
                savingLabel={t("admin.underwriting.modalApproved.saving")}
                cancelLabel={t("admin.underwriting.modalApproved.cancel")}
                disabled={!approvedDollars}
                saving={saving}
                error={modalErr}
            >
                <label className="uw-field">
                    <span className="uw-field-label mono">{t("admin.underwriting.modalApproved.valueLabel")}</span>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={approvedDollars}
                        onChange={(e) => setApprovedDollars(e.target.value.replace(/[^\d.]/g, ""))}
                        placeholder={t("admin.underwriting.modalApproved.valuePlaceholder")}
                        data-testid="uw-modal-approved-input"
                    />
                </label>
                <label className="uw-field">
                    <span className="uw-field-label mono">{t("admin.underwriting.modalApproved.notesLabel")}</span>
                    <textarea
                        rows={3}
                        value={approvedNotes}
                        onChange={(e) => setApprovedNotes(e.target.value)}
                        placeholder={t("admin.underwriting.modalApproved.notesPlaceholder")}
                        data-testid="uw-modal-approved-notes"
                    />
                </label>
            </OutcomeModal>

            <OutcomeModal
                open={effectiveOpen}
                onClose={() => !saving && setEffectiveOpen(false)}
                onSave={saveEffective}
                title={t("admin.underwriting.modalEffective.title")}
                sub={t("admin.underwriting.modalEffective.sub")}
                saveLabel={t("admin.underwriting.modalEffective.save")}
                savingLabel={t("admin.underwriting.modalEffective.saving")}
                cancelLabel={t("admin.underwriting.modalEffective.cancel")}
                disabled={!effectiveValue}
                saving={saving}
                error={modalErr}
            >
                <label className="uw-field">
                    <span className="uw-field-label mono">{t("admin.underwriting.modalEffective.valueLabel")}</span>
                    <input
                        type="date"
                        value={effectiveValue}
                        onChange={(e) => setEffectiveValue(e.target.value)}
                        data-testid="uw-modal-effective-input"
                    />
                </label>
                <label className="uw-field">
                    <span className="uw-field-label mono">{t("admin.underwriting.modalEffective.notesLabel")}</span>
                    <textarea
                        rows={3}
                        value={effectiveNotes}
                        onChange={(e) => setEffectiveNotes(e.target.value)}
                        placeholder={t("admin.underwriting.modalEffective.notesPlaceholder")}
                        data-testid="uw-modal-effective-notes"
                    />
                </label>
            </OutcomeModal>
        </div>
    );
}


function HistoryTable({ entries, kind, t, testId }) {
    const list = entries || [];
    return (
        <div className="uw-history">
            <div className="uw-history-title mono">{t("admin.underwriting.historyTitle")}</div>
            {list.length === 0 ? (
                <div className="uw-history-empty">{t("admin.underwriting.historyEmpty")}</div>
            ) : (
                <table className="uw-history-table" data-testid={testId}>
                    <thead>
                        <tr>
                            <th>{t("admin.underwriting.historyColumns.ts")}</th>
                            <th>{t("admin.underwriting.historyColumns.actor")}</th>
                            <th>{t("admin.underwriting.historyColumns.value")}</th>
                            <th>{t("admin.underwriting.historyColumns.notes")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[...list].reverse().map((row, i) => (
                            <tr key={`${row.ts}-${i}`}>
                                <td className="mono">{fmtStamp(row.ts)}</td>
                                <td className="mono">{row.actor_sub || "—"}</td>
                                <td>{kind === "money" ? (fmtMoney(row.value_cents) || "—") : (row.value || "—")}</td>
                                <td className="uw-history-notes">{row.notes || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
